/**
 * Team Topology Storage
 * IndexedDB persistence for team topology diagrams
 */

import type { TeamTopologyDiagram, TopologySnapshot } from './types';

const DB_NAME = 'team-topology-db';
const DB_VERSION = 1;
const STORE_NAME = 'diagrams';

let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB
 */
export async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[TeamTopology] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * Get all saved diagrams
 */
export async function getAllDiagrams(): Promise<TeamTopologyDiagram[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const diagrams = request.result.sort((a, b) => b.updatedAt - a.updatedAt);
      resolve(diagrams);
    };

    request.onerror = () => {
      console.error('[TeamTopology] Failed to get diagrams:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Get a single diagram by ID
 */
export async function getDiagram(id: string): Promise<TeamTopologyDiagram | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error('[TeamTopology] Failed to get diagram:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Save a diagram (create or update)
 */
export async function saveDiagram(diagram: TeamTopologyDiagram): Promise<void> {
  const db = await initDB();

  const updatedDiagram = {
    ...diagram,
    updatedAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(updatedDiagram);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error('[TeamTopology] Failed to save diagram:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Delete a diagram by ID
 */
export async function deleteDiagram(id: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error('[TeamTopology] Failed to delete diagram:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Create a new empty diagram
 */
export async function createDiagram(name: string): Promise<TeamTopologyDiagram> {
  const now = Date.now();
  const id = `topology-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const emptySnapshot: TopologySnapshot = {
    id: `snapshot-${now}`,
    name: 'Current State',
    date: new Date().toISOString().split('T')[0],
    teams: [],
    interactions: [],
  };

  const diagram: TeamTopologyDiagram = {
    id,
    name,
    currentState: emptySnapshot,
    targetStates: [],
    createdAt: now,
    updatedAt: now,
  };

  await saveDiagram(diagram);
  return diagram;
}

/**
 * Duplicate a diagram
 */
export async function duplicateDiagram(
  id: string,
  newName?: string
): Promise<TeamTopologyDiagram | null> {
  const original = await getDiagram(id);
  if (!original) return null;

  const now = Date.now();
  const newId = `topology-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const duplicate: TeamTopologyDiagram = {
    ...original,
    id: newId,
    name: newName || `${original.name} (Copy)`,
    createdAt: now,
    updatedAt: now,
  };

  await saveDiagram(duplicate);
  return duplicate;
}

/**
 * Export diagram to JSON
 */
export function exportToJSON(diagram: TeamTopologyDiagram): string {
  return JSON.stringify(diagram, null, 2);
}

/**
 * Import diagram from JSON
 */
export async function importFromJSON(json: string): Promise<TeamTopologyDiagram> {
  const parsed = JSON.parse(json) as TeamTopologyDiagram;

  const now = Date.now();
  const newId = `topology-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const diagram: TeamTopologyDiagram = {
    ...parsed,
    id: newId,
    name: parsed.name || 'Imported Diagram',
    createdAt: now,
    updatedAt: now,
  };

  await saveDiagram(diagram);
  return diagram;
}

/**
 * Get count of saved diagrams
 */
export async function getDiagramCount(): Promise<number> {
  const diagrams = await getAllDiagrams();
  return diagrams.length;
}

/**
 * Add a target state (evolution snapshot) to a diagram
 */
export async function addTargetState(
  diagramId: string,
  snapshot: TopologySnapshot
): Promise<TeamTopologyDiagram | null> {
  const diagram = await getDiagram(diagramId);
  if (!diagram) return null;

  const updated: TeamTopologyDiagram = {
    ...diagram,
    targetStates: [...diagram.targetStates, snapshot],
    updatedAt: Date.now(),
  };

  await saveDiagram(updated);
  return updated;
}

/**
 * Remove a target state from a diagram
 */
export async function removeTargetState(
  diagramId: string,
  snapshotId: string
): Promise<TeamTopologyDiagram | null> {
  const diagram = await getDiagram(diagramId);
  if (!diagram) return null;

  const updated: TeamTopologyDiagram = {
    ...diagram,
    targetStates: diagram.targetStates.filter(s => s.id !== snapshotId),
    updatedAt: Date.now(),
  };

  await saveDiagram(updated);
  return updated;
}
